// src/components/dashboard/UpcomingIncome.tsx 
import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Stack,
  CircularProgress,
  Divider,
} from '@mui/material';
import { useUpcomingIncome } from '../../hooks/useUpcomingIncome';
import { useFormatting } from '../../context/FormattingContext';
import { formatDateInTimeZone, formatMoney } from '../../utils/formatting';

const UpcomingIncome: React.FC = () => {
  const { upcoming, loading, error } = useUpcomingIncome(30); // next 30 days
  const formatting = useFormatting();

  const totalAmount = upcoming.reduce((sum, inc) => sum + (inc.amount || 0), 0);

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" fontWeight="bold">💵 Upcoming Income</Typography>

      {loading ? (
        <CircularProgress size={20} sx={{ mt: 1 }} />
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : upcoming.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No income expected in the next 30 days.</Typography>
      ) : (
        <>
          <Stack spacing={1} sx={{ mt: 1 }}>
            {upcoming.map(inc => {
              const dt = inc.date;
              if (!dt) return null;
              return (
                <Box key={inc._id} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography>{inc.description || inc.category || 'Income'}</Typography>
                  <Typography>
                    {formatMoney(inc.amount, formatting)} on {formatDateInTimeZone(dt, {
                      locale: formatting.locale,
                      timeZone: formatting.timeZone,
                      dateStyle: 'medium',
                    })}
                  </Typography>
                </Box>
              );
            })}
          </Stack>

          <Divider sx={{ my: 2 }} />

          <Typography variant="body2" fontWeight="medium">
            Total expected income over the next 30 days: <strong>{formatMoney(totalAmount, formatting)}</strong>
          </Typography>
        </>
      )}
    </Paper>
  );
};

export default UpcomingIncome;
